// src/components/Drones/DronePermissionsManager.js
import { useEffect, useState, useCallback } from "react";
import { API_URL } from "../../config";
import './Drones.css';
import { authFetch } from "../../utils";

export default function DronesPermissionsManager({ drone, can }) {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingUsername, setUpdatingUsername] = useState(null);
  const [successUsername, setSuccessUsername] = useState(null);
  const [activeViewers, setActiveViewers] = useState([]);

  const [guestLinks, setGuestLinks] = useState([]);
  const [guestLabel, setGuestLabel] = useState('');
  const [guestHours, setGuestHours] = useState('');
  const [creatingGuest, setCreatingGuest] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [guestHoursError, setGuestHoursError] = useState(null);

  const fetchActiveViewers = useCallback(async () => {
  try {
    const resp = await authFetch(
      `${API_URL}drones/active-viewers/${drone.droneId}`
    );

    if (!resp.ok) throw new Error("Error al obtener viewers activos");

    const data = await resp.json();
    const map = {};
    data.forEach(u => {
      map[u.username] = u.isWatching;
    });

    setActiveViewers(map);
  } catch (err) {
    console.error(err);
  }
}, [drone.droneId]);


  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await authFetch(`${API_URL}drones/view-permission/${drone.droneId}/`);
      if (!resp.ok) throw new Error("Error al obtener permisos");
      const data = await resp.json();
      setPermissions(data);
    } catch (err) {
      console.error(err);
      setError(err.message || "Error desconocido");
    } finally {
      setLoading(false);
    }
  }, [drone.droneId]);

  useEffect(() => {
    fetchPermissions();
    fetchActiveViewers();

    // Intervalo cada 5 segundos
    const intervalId = setInterval(() => {
      fetchActiveViewers();
    }, 5000);

    // Limpiar intervalo al desmontar
    return () => clearInterval(intervalId);
  }, [fetchPermissions, fetchActiveViewers]);

  const fetchGuestLinks = useCallback(async () => {
    try {
      const resp = await authFetch(`${API_URL}drones/guest-links/${drone.droneId}`);
      if (!resp.ok) return;
      setGuestLinks(await resp.json());
    } catch (err) {
      console.error(err);
    }
  }, [drone.droneId]);

  useEffect(() => {
    fetchGuestLinks();
  }, [fetchGuestLinks]);

  const handleCreateGuestLink = async () => {
    const hours = parseInt(guestHours, 10);
    if (!hours || hours <= 0) return;
    if (hours > 72) {
      setGuestHoursError('El máximo son 72 horas');
      return;
    }
    setGuestHoursError(null);
    setCreatingGuest(true);
    try {
      const resp = await authFetch(`${API_URL}drones/guest-link`, {
        method: "POST",
        body: JSON.stringify({ droneId: drone.droneId, expiresInHours: hours, label: guestLabel }),
      });
      if (!resp.ok) throw new Error("Error al crear enlace");
      setGuestLabel('');
      setGuestHours('');
      await fetchGuestLinks();
    } catch (err) {
      alert(err.message || "Error al crear enlace de invitado");
    } finally {
      setCreatingGuest(false);
    }
  };

  const handleDeleteGuestLink = async (token) => {
    if (!window.confirm("¿Eliminar este enlace? Quedará inaccesible de inmediato.")) return;
    try {
      await authFetch(`${API_URL}drones/guest-link/${token}`, { method: "DELETE" });
      setGuestLinks(prev => prev.filter(l => l.token !== token));
    } catch (err) {
      alert("Error al eliminar enlace");
    }
  };

  const handleCopyLink = (token) => {
    const url = `${window.location.origin}/drone-guest?t=${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleUpdateExpires = async (username, hours) => {
    if (hours === undefined || hours < 0) return;

    setUpdatingUsername(username);
    try {
      const resp = await authFetch(`${API_URL}drones/view-permission`, {
        method: "PUT",
        body: JSON.stringify({ expiresInHours: hours, username, droneId: drone.droneId }),
      });
      if (!resp.ok) throw new Error("Error al actualizar permisos");

      // Mostrar check temporal
      setSuccessUsername(username);
      setTimeout(() => setSuccessUsername(null), 2000);

      // Refrescar la lista
      await fetchPermissions();
    } catch (err) {
      console.error(err);
      alert(err.message || "Error al actualizar");
    } finally {
      setUpdatingUsername(null);
    }
  };

  if (loading) return <p style={{ textAlign: "center" }}>Cargando permisos...</p>;
  if (error) return <p style={{ color: "red", textAlign: "center" }}>{error}</p>;
  if (!permissions.length) return <p style={{ textAlign: "center" }}>No hay permisos para este dron.</p>;

  const now = new Date();

  const handleDownloadLogs = async () => {
    const resp = await authFetch(`${API_URL}drones/view-logs/${drone.droneId}?days=15`);
    const logs = await resp.json();

    const actionLabel = {
      'DRONE_VIEW': 'Visualización',
      'DRONE_VIEW_PERMISSION_ADD': 'Permiso añadido',
      'DRONE_VIEW_PERMISSION_UPDATE': 'Permiso actualizado',
      'DRONE_VIEW_PERMISSION_DELETE': 'Permiso eliminado',
    };

    const rows = [
      ['Fecha/hora', 'Acción', 'Usuario', 'Destinatario', 'Dron'],
      ...logs.map(l => [
        new Date(l.timestamp).toLocaleString(),
        actionLabel[l.action] || l.action,
        l.username || '',
        l.meta?.targetUsername || '',
        l.meta?.droneId || drone.droneId,
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs_${drone.droneId}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ maxWidth: "700px", margin: "2rem auto", padding: "0 1rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <h2 style={{ margin: 0 }}>Permisos para {drone.name}</h2>
        <button
          onClick={handleDownloadLogs}
          style={{ padding: "0.4rem 0.8rem", backgroundColor: "#6c757d", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.85rem" }}
        >
          ⬇ Registro actividad 15 días
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {permissions.map((perm) => {
          const isExpired = new Date(perm.expiresAt) < now;

          return (
            <div
              key={perm.username}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1rem",
                borderRadius: "8px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                backgroundColor: "#fff",
                transition: "transform 0.2s",
                border: "1px solid #eee",
              }}
            >
              <div>
                <p
                  style={{
                    margin: 0,
                    fontWeight: "bold",
                    color: "#000",
                  }}
                >
                  {perm.fullname} 
                  {isExpired && <span> ⛔</span>}
                </p>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "0.9rem",
                    color: "#555",
                  }}
                >
                  Válido hasta: 
                  <span style={{ color: isExpired ? "red" : "#555" }}>{new Date(perm.expiresAt).toLocaleString()}</span>
                </p>
              </div>

              {can(['drone-manager','admin']) && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  {successUsername === perm.username && (
                    <span style={{ color: "green", fontSize: "1.5rem" }}>✅</span>
                  )}
                  <input
                    type="number"
                    min="0"
                    placeholder="Horas"
                    style={{
                      width: "70px",
                      padding: "0.25rem 0.5rem",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      outline: "none",
                      color: "#000",
                      backgroundColor: "#fff",
                    }}
                    id={`hours-${perm.username}`}
                  />
                  <button
                    disabled={updatingUsername === perm.username}
                    onClick={() => {
                      const input = document.getElementById(`hours-${perm.username}`);
                      const hours = parseInt(input.value, 10);
                      handleUpdateExpires(perm.username, hours);
                      input.value = "";
                    }}
                    style={{
                      padding: "0.5rem 1rem",
                      backgroundColor: updatingUsername === perm.username ? "#007bff" : "#007bff",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                  >
                    {updatingUsername === perm.username ? "..." : "Actualizar permiso"}
                  </button>
                  <span
                    title={
                      activeViewers[perm.username]
                        ? "Visualizando el streaming"
                        : "No está visualizando"
                    }
                    className={
                      activeViewers[perm.username]
                        ? "streaming-indicator"
                        : ""
                    }
                    style={
                      !activeViewers[perm.username]
                        ? {
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            backgroundColor: "#ccc",
                            display: "inline-block",
                            marginLeft: "8px",
                            border: "1px solid #999",
                          }
                        : { marginLeft: "8px" }
                    }
                  />

                  { activeViewers[perm.username] && (
                  <button
                    onClick={async () => {
                      try {
                        const resp = await authFetch(`${API_URL}drones/close-sessions`, {
                          method: "POST",                          
                          body: JSON.stringify({ username: perm.username, droneId: drone.droneId }),
                        });
                        if (!resp.ok) throw new Error("Error al cerrar sesiones");
                        const data = await resp.json();
                        alert(data.message);
                        // refrescar viewers activos
                        await fetchActiveViewers();
                      } catch (err) {
                        console.error(err);
                        alert(err.message || "Error al cerrar sesiones");
                      }
                    }}
                    style={{
                      padding: "0.25rem 0.5rem",
                      backgroundColor: "#e74c3c",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Parar visualización
                  </button>
                  )}

                </div>
              )}
            </div>
          );
        })}
      </div>

      {can(['drone-manager', 'admin']) && (
        <div style={{ marginTop: "2.5rem", borderTop: "1px solid #ddd", paddingTop: "1.5rem" }}>
          <h3 style={{ marginBottom: "1rem" }}>🔗 Enlaces de invitado</h3>

          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1rem", alignItems: "center" }}>
            <input
              type="text"
              placeholder="Etiqueta (opcional)"
              value={guestLabel}
              onChange={e => setGuestLabel(e.target.value)}
              style={{ padding: "0.4rem 0.6rem", borderRadius: "4px", border: "1px solid #ccc", flex: "1", minWidth: "140px" }}
            />
            <input
              type="number"
              min="1"
              max="72"
              placeholder="Horas"
              value={guestHours}
              onChange={e => setGuestHours(e.target.value)}
              style={{ padding: "0.4rem 0.6rem", borderRadius: "4px", border: "1px solid #ccc", width: "80px" }}
            />
            {guestHoursError && (
              <span style={{ color: "#dc3545", fontSize: "0.85rem", width: "100%" }}>{guestHoursError}</span>
            )}
            <button
              onClick={handleCreateGuestLink}
              disabled={creatingGuest}
              style={{ padding: "0.4rem 0.9rem", backgroundColor: "#28a745", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}
            >
              {creatingGuest ? "..." : "Crear enlace"}
            </button>
          </div>

          {guestLinks.length === 0 ? (
            <p style={{ color: "#888", fontSize: "0.9rem" }}>No hay enlaces activos.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {guestLinks.map(link => {
                const expired = new Date(link.expiresAt) < new Date();
                const isWatching = !!activeViewers[`guest-${link.token.slice(0, 8)}`];
                return (
                  <li key={link.token} style={{
                    display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap",
                    padding: "0.6rem 0.8rem", backgroundColor: "#fff", borderRadius: "6px",
                    border: `1px solid ${expired ? "#f5c6cb" : "#c3e6cb"}`,
                    opacity: expired ? 0.6 : 1
                  }}>
                    <span style={{ flex: 1, fontSize: "0.9rem", minWidth: "120px" }}>
                      {link.label || <em style={{ color: "#aaa" }}>Sin etiqueta</em>}
                      {expired && <span style={{ color: "#dc3545", marginLeft: "0.4rem" }}>⛔ expirado</span>}
                    </span>
                    <span style={{ fontSize: "0.8rem", color: "#666" }}>
                      Caduca: {new Date(link.expiresAt).toLocaleString()}
                    </span>
                    <button
                      onClick={() => handleCopyLink(link.token)}
                      style={{ padding: "0.3rem 0.6rem", backgroundColor: copiedToken === link.token ? "#28a745" : "#007bff", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.85rem" }}
                    >
                      {copiedToken === link.token ? "✓ Copiado" : "Copiar enlace"}
                    </button>
                    <button
                      onClick={() => handleDeleteGuestLink(link.token)}
                      style={{ padding: "0.3rem 0.6rem", backgroundColor: "#dc3545", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.85rem" }}
                    >
                      Eliminar
                    </button>
                    <span
                      title={isWatching ? "Visualizando el streaming" : "No está visualizando"}
                      className={isWatching ? "streaming-indicator" : ""}
                      style={!isWatching ? { width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#ccc", display: "inline-block", border: "1px solid #999", flexShrink: 0 } : { flexShrink: 0 }}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
